import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';

import { User } from '../model';
import { setAccessTokenGetter } from '../../../shared/api/client';
import { profileApi, UserProfile } from '../endpoints';
import { SIGNIN_HINT_KEY, clearSessionScopedStorage } from '../sessionScopedStorage';

// Logs the user out of Thinkific via a hidden iframe hitting its Devise sign_out
// URL. Fire-and-forget so an unreachable Thinkific can't block our own logout.
function fireThinkificLogout(url: string) {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  iframe.onload = () => iframe.remove();
  document.body.appendChild(iframe);
  setTimeout(() => iframe.remove(), 3000);
}

interface SigninHint {
  hint: string;
  setAt: string;
}

// Account switching has no native Auth0 path, so we hack it: write a hint to
// SIGNIN_HINT_KEY, log out, then on the anonymous reload consume the hint and
// re-login with login_hint. Auth0's login_hint is only a hint — it won't
// override an active IdP session — so worst case the user re-picks from
// Google's picker.
function readSigninHint(): SigninHint | null {
  try {
    const raw = localStorage.getItem(SIGNIN_HINT_KEY);
    return raw ? (JSON.parse(raw) as SigninHint) : null;
  } catch {
    return null;
  }
}

function clearSigninHint() {
  localStorage.removeItem(SIGNIN_HINT_KEY);
}

// The API rejects sign-in for an unverified address with HTTP 403 and this
// stable discriminator in the body (see ApiErrorResponses#render_api_error).
function isEmailVerificationError(err: unknown): boolean {
  return (err as { response?: { data?: { code?: string } } } | null)
    ?.response?.data?.code === 'email_verification_required';
}

/**
 * `forceIdentityPrompt` adds Auth0's `prompt=login`, which makes Auth0 ASK who
 * this is instead of answering silently from an existing SSO session.
 *
 * On a shared machine a plain /authorize is satisfied by whoever signed in
 * last, so someone following a link meant for them is logged straight into a
 * colleague's account and never sees a sign-up screen at all. `screen_hint`
 * cannot help: with a live session no screen renders, so there is nothing to
 * hint at. Only `prompt=login` changes that decision.
 *
 * DEFAULTS TO TRUE WHENEVER A HINT IS SUPPLIED. Passing a login_hint is the
 * caller saying "this link belongs to one particular person", and that is
 * exactly when a stale session must not answer on their behalf. Making it
 * opt-in instead meant every future caller had to remember — and the two that
 * already existed had not, which is how the same incident kept recurring.
 * Pass `false` explicitly for the rare flow where any signed-in identity will
 * do (an emailed invoice, say, which is payable by whoever holds the link).
 */
export interface AuthPromptOptions {
  forceIdentityPrompt?: boolean;
}

export interface AuthContextValue {
  user: User | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  needsEmailVerification: boolean;
  login: (hint?: string, opts?: AuthPromptOptions) => void;
  loginWithSignup: (hint?: string, opts?: AuthPromptOptions) => void;
  logout: (returnTo?: string) => void;
  updateUser: (fields: Partial<User>) => void;
  switchToAccount: (email: string) => void;
}

export const AuthenticationContext = createContext<AuthContextValue>({
  user: undefined,
  isLoading: true,
  isAuthenticated: false,
  needsEmailVerification: false,
  login: () => {},
  loginWithSignup: () => {},
  logout: () => {},
  updateUser: () => {},
  switchToAccount: () => {},
});

export function AuthenticationContextProvider({ children }: { children: React.ReactNode }) {
  const {
    user: auth0User,
    isLoading: auth0Loading,
    isAuthenticated,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const queryClient = useQueryClient();
  const [backendUser, setBackendUser] = useState<User | undefined>(undefined);
  const [registering, setRegistering] = useState(false);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const signinHintHandledRef = useRef(false);

  const auth0Data: User | undefined = auth0User ? {
    id: auth0User.sub,
    email: auth0User.email,
    name: auth0User.name,
    first_name: auth0User.given_name,
    last_name: auth0User.family_name,
  } : undefined;

  const user: User | undefined = auth0Data
    ? { ...auth0Data, ...backendUser }
    : backendUser;

  const isLoading = auth0Loading || registering;

  const login = useCallback((hint?: string, opts?: AuthPromptOptions) => {
    const forcePrompt = opts?.forceIdentityPrompt ?? !!hint;
    loginWithRedirect({
      authorizationParams: {
        ...(hint && { login_hint: hint }),
        ...(forcePrompt && { prompt: 'login' }),
      },
    });
  }, [loginWithRedirect]);

  const loginWithSignup = useCallback((hint?: string, opts?: AuthPromptOptions) => {
    const forcePrompt = opts?.forceIdentityPrompt ?? !!hint;
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
        ...(hint && { login_hint: hint }),
        ...(forcePrompt && { prompt: 'login' }),
      },
    });
  }, [loginWithRedirect]);

  const logout = useCallback((returnTo?: string) => {
    const profile = queryClient.getQueryData<UserProfile>(['profile']);
    if (profile?.thinkific_logout_url) fireThinkificLogout(profile.thinkific_logout_url);
    clearSessionScopedStorage();
    setBackendUser(undefined);
    setNeedsEmailVerification(false);
    auth0Logout({ logoutParams: { returnTo: returnTo || window.location.origin } });
  }, [auth0Logout, queryClient]);

  const updateUser = useCallback((fields: Partial<User>) => {
    setBackendUser((prev) => ({ ...prev, ...fields }) as User);
  }, []);

  const switchToAccount = useCallback((email: string) => {
    if (!email) return;
    // Clear BEFORE writing the hint: the hint is session-scoped too.
    clearSessionScopedStorage();
    try {
      localStorage.setItem(SIGNIN_HINT_KEY, JSON.stringify({ hint: email, setAt: new Date().toISOString() }));
    } catch {
      // localStorage unavailable (private browsing) — fall back to a plain
      // logout; the user signs in manually.
    }
    const profile = queryClient.getQueryData<UserProfile>(['profile']);
    if (profile?.thinkific_logout_url) fireThinkificLogout(profile.thinkific_logout_url);
    setBackendUser(undefined);
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout, queryClient]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Auth0 SDK silent renewal can't satisfy these — kick to interactive login.
    const wrappedGetter = async () => {
      try {
        return await getAccessTokenSilently();
      } catch (err) {
        const code = (err as { error?: string } | null)?.error;
        if (code === 'consent_required' || code === 'login_required' || code === 'missing_refresh_token') {
          loginWithRedirect();
        }
        throw err;
      }
    };
    setAccessTokenGetter(wrappedGetter);
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  // Consume the one-shot sign-in hint left by switchToAccount on the anonymous
  // reload (see SIGNIN_HINT_KEY).
  useEffect(() => {
    if (auth0Loading || isAuthenticated) return;
    if (signinHintHandledRef.current) return;

    const hintRecord = readSigninHint();
    if (!hintRecord?.hint) return;

    signinHintHandledRef.current = true;
    clearSigninHint();
    loginWithRedirect({
      authorizationParams: { login_hint: hintRecord.hint, prompt: 'login' },
    });
  }, [auth0Loading, isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    if (!isAuthenticated || auth0Loading || backendUser || needsEmailVerification) return;

    setRegistering(true);
    profileApi.register({
      email: auth0User?.email || auth0User?.name,
      first_name: auth0User?.given_name || auth0User?.nickname,
      last_name: auth0User?.family_name,
    })
      .then((u) => {
        setBackendUser({
          id: String(u.id),
          email: u.email_address,
          name: u.full_name,
          first_name: u.first_name ?? undefined,
          last_name: u.last_name ?? undefined,
          roles: u.role ? [u.role] : [],
        });
      })
      .catch((err) => {
        // Unverified email: surface a "check your inbox" screen rather than
        // silently swallowing it and leaving the app on the loading spinner.
        if (isEmailVerificationError(err)) setNeedsEmailVerification(true);
      })
      .finally(() => setRegistering(false));
  }, [isAuthenticated, auth0Loading, backendUser, needsEmailVerification, auth0User]);

  return (
    <AuthenticationContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        needsEmailVerification,
        login,
        loginWithSignup,
        logout,
        updateUser,
        switchToAccount,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
}

export const useAuth = () => useContext(AuthenticationContext);
